import type {
  InventoryItemRecord,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';
import type { MenuItem } from '@/renderer/types/menu';
import { Button, DropdownSelect, Modal } from '@components/common';
import { FormInput, FormTextarea } from '@components/common/FormField';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  applyItemToLine,
  buildSaveRecipePayload,
  createEmptyRecipeLine,
  RecipeFormState,
  RecipeLineFormState,
} from './recipeForm';

interface RecipeEditorModalProps {
  initial: RecipeFormState;
  isSaving: boolean;
  title: string;
  menuItems: MenuItem[];
  menuItemsHasMore: boolean;
  menuItemsLoading: boolean;
  onMenuItemsSearchChange: (value: string) => void;
  onLoadMoreMenuItems: () => void;
  inventoryItems: InventoryItemRecord[];
  inventoryItemsHasMore: boolean;
  inventoryItemsLoading: boolean;
  onInventoryItemsSearchChange: (value: string) => void;
  onLoadMoreInventoryItems: () => void;
  units: InventoryUnitRecord[];
  companyId: number;
  onClose: () => void;
  onSubmit: (payload: ReturnType<typeof buildSaveRecipePayload>) => void;
  existingId?: string;
}

export function RecipeEditorModal({
  initial,
  isSaving,
  title,
  menuItems,
  menuItemsHasMore,
  menuItemsLoading,
  onMenuItemsSearchChange,
  onLoadMoreMenuItems,
  inventoryItems,
  inventoryItemsHasMore,
  inventoryItemsLoading,
  onInventoryItemsSearchChange,
  onLoadMoreInventoryItems,
  units,
  companyId,
  onClose,
  onSubmit,
  existingId,
}: RecipeEditorModalProps) {
  const [form, setForm] = useState<RecipeFormState>(initial);
  // Tracks whether the user has typed a name themselves, so a later menu
  // item selection doesn't clobber it. Not set for the initial pre-fill
  // (from `initial.name`, e.g. the Missing Recipes page's quick action) —
  // only an explicit edit or another menu-item selection counts as "the
  // user is now driving the name field."
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);

  const setField = <K extends keyof RecipeFormState>(
    key: K,
    value: RecipeFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateLine = (tempId: string, patch: Partial<RecipeLineFormState>) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((l) =>
        l.tempId === tempId ? { ...l, ...patch } : l,
      ),
    }));
  };

  const removeLine = (tempId: string) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((l) => l.tempId !== tempId),
    }));
  };

  const handleItemSelect = (tempId: string, itemId: string) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    if (!item) return;
    const line = form.lines.find((l) => l.tempId === tempId);
    if (!line) return;
    updateLine(tempId, applyItemToLine(line, item));
  };

  const handleMenuItemSelect = (itemId: string) => {
    const item = menuItems.find((candidate) => candidate.id === itemId);
    setForm((prev) => ({
      ...prev,
      menuItemId: itemId,
      menuItemLabel: item?.name ?? prev.menuItemLabel,
      // Staff almost always name the recipe after the menu item — default
      // it here so they don't have to retype it, but never overwrite a
      // name the user has actually typed themselves.
      name: !nameManuallyEdited && item?.name ? item.name : prev.name,
    }));
  };

  const unitLabel = (unitId: string) =>
    units.find((u) => u.id === unitId)?.abbreviation ??
    units.find((u) => u.id === unitId)?.name ??
    '';

  const canSubmit =
    form.menuItemId &&
    form.name.trim() &&
    form.lines.some((l) => l.inventoryItemId && parseFloat(l.quantity) > 0);

  const handleSubmit = () => {
    if (!canSubmit || isSaving) return;
    onSubmit(buildSaveRecipePayload(companyId, form, existingId));
  };

  const menuItemOptions = [
    { value: '', label: 'Select menu item…' },
    ...menuItems.map((m) => ({ value: m.id, label: m.name })),
  ];

  const inventoryItemOptions = [
    { value: '', label: 'Select ingredient…' },
    ...inventoryItems.map((i) => ({ value: i.id, label: i.name })),
  ];

  return (
    <Modal
      isOpen
      title={title}
      size="xl"
      onClose={isSaving ? () => {} : onClose}
    >
      <div className="flex flex-col gap-6 p-6">
        {/* Header fields */}
        <div className="grid gap-4 md:grid-cols-2">
          <DropdownSelect
            label="Menu item *"
            value={form.menuItemId}
            options={menuItemOptions}
            customDisplayLabel={form.menuItemLabel}
            searchable
            searchPlaceholder="Search menu items…"
            onSearchChange={onMenuItemsSearchChange}
            hasMoreOptions={menuItemsHasMore}
            isLoadingOptions={menuItemsLoading}
            onLoadMoreOptions={onLoadMoreMenuItems}
            onChange={(v) => handleMenuItemSelect(v as string)}
          />
          <FormInput
            label="Recipe name *"
            value={form.name}
            onChange={(e) => {
              setNameManuallyEdited(true);
              setField('name', e.target.value);
            }}
            placeholder="e.g. Beef Burger Standard"
          />
          <FormInput
            label="Yield quantity"
            type="number"
            value={form.yieldQuantity}
            onChange={(e) => setField('yieldQuantity', e.target.value)}
            placeholder="1"
            min={0.0001}
            step={0.0001}
          />
          <DropdownSelect
            label="Status"
            value={form.isActive ? 'active' : 'inactive'}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            onChange={(v) => setField('isActive', v === 'active')}
          />
        </div>
        <FormTextarea
          label="Description"
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="Optional notes about this recipe…"
          rows={2}
        />

        {/* Ingredients */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">
              Ingredients (per {form.yieldQuantity || '1'} portion)
            </p>
            <Button
              variant="text"
              size="sm"
              onClick={() =>
                setField('lines', [...form.lines, createEmptyRecipeLine()])
              }
            >
              <Plus size={14} />
              Add ingredient
            </Button>
          </div>

          <div className="rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Ingredient
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Qty (stock unit)
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Unit
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Notes
                  </th>
                  <th className="px-4 py-2" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line) => (
                  <tr key={line.tempId} className="border-t border-gray-100">
                    <td className="px-4 py-2">
                      <DropdownSelect
                        label=""
                        value={line.inventoryItemId}
                        options={inventoryItemOptions}
                        customDisplayLabel={line.inventoryItemLabel}
                        searchable
                        searchPlaceholder="Search ingredients…"
                        onSearchChange={onInventoryItemsSearchChange}
                        hasMoreOptions={inventoryItemsHasMore}
                        isLoadingOptions={inventoryItemsLoading}
                        onLoadMoreOptions={onLoadMoreInventoryItems}
                        onChange={(v) =>
                          handleItemSelect(line.tempId, v as string)
                        }
                      />
                    </td>
                    <td className="px-4 py-2">
                      <FormInput
                        label=""
                        type="number"
                        value={line.quantity}
                        onChange={(e) =>
                          updateLine(line.tempId, { quantity: e.target.value })
                        }
                        placeholder="0"
                        min={0}
                        step={0.0001}
                      />
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {unitLabel(line.unitId) || '—'}
                    </td>
                    <td className="px-4 py-2">
                      <FormInput
                        label=""
                        value={line.notes}
                        onChange={(e) =>
                          updateLine(line.tempId, { notes: e.target.value })
                        }
                        placeholder="Optional…"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        variant="text"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => removeLine(line.tempId)}
                        disabled={form.lines.length <= 1}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isSaving}
            isLoading={isSaving}
          >
            {existingId ? 'Save changes' : 'Create recipe'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
